import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { LocalStorageFacade } from '@application/localstorage';
import { TranslateService } from '@ngx-translate/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { LanguageService } from './infra/language.service';
import { tapResponse } from '@ngrx/operators';
import { configureDevExtremeLocalization } from '@shared/assets';

export const languages = [
  { id: "en-US", description: "PROFILEMENU.LANGUAGES.ENGLISH" },
  { id: "pt-BR", description: "PROFILEMENU.LANGUAGES.PORTUGUESE" },
  { id: "es-ES", description: "PROFILEMENU.LANGUAGES.SPANISH"}
];

export const defaultLanguage = 'en-US';

export interface ILanguageStore {
  language: string;
}

export const initialLanguageStore: ILanguageStore = {
  language: null,
};

export const LanguageStore = signalStore(
  {providedIn: 'root'},
  withState(initialLanguageStore),
  withComputed((state) => ({
    getCurrentLanguage: computed(() => state.language())
    })
  ),
  withMethods((
    state,
    localStorage = inject(LocalStorageFacade),
    translateService = inject(TranslateService),
    languageService = inject(LanguageService),
  ) => {

    const verifyLanguage = (language: string): boolean => {
      return languages.some(({ id }) => id === language);
    }

    const setLanguage = (language: string): void => {
      if (!verifyLanguage(language)) language = defaultLanguage;
      if (language === state.language()) return;
      patchState(state, { language });
      localStorage.setItem('language', language);
      translateService.use(language);
      configureDevExtremeLocalization(language);
      languageService.setLanguageInAccountApp(language);
    };

    return {
      setLanguage,
      verifyLanguage,
      getLocalization: rxMethod<void>(pipe(
        switchMap(() => languageService.getLocalization().pipe(
          tapResponse({
            next: (localization) => {
              let language = `${localization?.Language}-${localization?.CountryCode}`;
              const hasLang = verifyLanguage(language);
              if (!hasLang) language = languages.find(({ id }) => localization?.Language && id.startsWith(localization?.Language))?.id || defaultLanguage;
              setLanguage(language)
            },
            error: () => setLanguage(defaultLanguage)
          })
        ))
      ))

    }
  }),
  withHooks({
    onInit(
      state,
      localStorage = inject(LocalStorageFacade),
      translateService = inject(TranslateService),
      languageService = inject(LanguageService),
    ) {
      translateService.setDefaultLang(defaultLanguage);
      const language = localStorage.getItem('language');

      if (language && state.verifyLanguage(language)) {
        state.setLanguage(language);
        return;
      }
      
      configureDevExtremeLocalization(defaultLanguage);
      languageService.setLanguageInAccountApp(defaultLanguage);
      translateService.use(defaultLanguage).subscribe(() => {
        state.getLocalization();
      });

      patchState(state, { language: defaultLanguage });
    }
  })
);
